import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import {
  FlaskConical,
  Calendar,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Timer,
  StickyNote,
  CalendarPlus,
  MapPin,
  CheckCircle2,
  Sun,
  Moon,
  Printer,
  Users,
  Search,
} from "lucide-react";
import { SidebarProvider } from "@/components/ui/sidebar";
import Teacherheader from "../Teacherheader";
import Teachersidebar from "../Teachersidebar";
import { useSelector } from 'react-redux';
import { selectUsers } from '../../State/usersSlice';

interface LabSession {
  id: string;
  teacherName: string;
  date: string;
  startTime: string;
  endTime: string;
  period: string;
  labName: string;
  labType: string;
  className: string;
  subject: string;
  topic?: string;
  notes?: string;
  studentCount?: number;
  students?: string[];
}

interface LabInfo {
  name: string;
  type: string;
  capacity: number;
  features: string[];
}

interface BreakageReport {
  id: string;
  labName: string;
  date: string;
  item: string;
  status: "pending" | "resolved";
}

interface SessionNote {
  sessionId: string;
  note: string;
  checklist: { prepared: boolean; checked: boolean };
}

const sampleSessions: LabSession[] = [
  {
    id: "1",
    teacherName: "Mr. Johnson",
    date: new Date().toISOString().split('T')[0],
    startTime: "08:00",
    endTime: "09:00",
    period: "Period 1",
    labName: "Chemistry Lab",
    labType: "chemistry",
    className: "Class 10A",
    subject: "Chemistry",
    topic: "Acid-Base Titration",
    notes: "Bring safety goggles, prepare indicators",
    studentCount: 28,
    students: ["Alice Brown", "Bob Smith", "Carol White", "David Lee"]
  },
  {
    id: "2",
    teacherName: "Mr. Johnson",
    date: new Date().toISOString().split('T')[0],
    startTime: "10:30",
    endTime: "11:30",
    period: "Period 3",
    labName: "Physics Lab",
    labType: "physics",
    className: "Class 11B",
    subject: "Physics",
    topic: "Optics Experiment",
    studentCount: 25
  },
  {
    id: "3",
    teacherName: "Mr. Johnson",
    date: new Date(Date.now() + 86400000).toISOString().split('T')[0],
    startTime: "09:00",
    endTime: "10:00",
    period: "Period 2",
    labName: "Computer Lab 1",
    labType: "computer",
    className: "Class 9C",
    subject: "Computer Science",
    topic: "Python Programming",
    studentCount: 30
  },
  {
    id: "4",
    teacherName: "Mr. Johnson",
    date: new Date(Date.now() + 86400000 * 2).toISOString().split('T')[0],
    startTime: "14:00",
    endTime: "15:00",
    period: "Period 6",
    labName: "Biology Lab",
    labType: "biology",
    className: "Class 12A",
    subject: "Biology",
    topic: "Microscopy Practical",
    notes: "Practical exam - ensure microscopes are calibrated",
    studentCount: 22
  },
  {
    id: "5",
    teacherName: "Ms. Smith",
    date: new Date().toISOString().split('T')[0],
    startTime: "11:30",
    endTime: "12:30",
    period: "Period 4",
    labName: "Chemistry Lab",
    labType: "chemistry",
    className: "Class 10B",
    subject: "Chemistry",
    studentCount: 26
  },
  {
    id: "6",
    teacherName: "Mr. Johnson",
    date: new Date(Date.now() - 86400000 * 2).toISOString().split('T')[0],
    startTime: "08:00",
    endTime: "09:00",
    period: "Period 1",
    labName: "Physics Lab",
    labType: "physics",
    className: "Class 11A",
    subject: "Physics",
    topic: "Pendulum Experiment",
    studentCount: 24
  }
];

const labInfoData: LabInfo[] = [
  { name: "Chemistry Lab", type: "chemistry", capacity: 30, features: ["Fume hood", "Eye wash station", "Gas lines", "Chemical storage"] },
  { name: "Physics Lab", type: "physics", capacity: 28, features: ["Optical bench", "Oscilloscopes", "Power supplies", "Darkroom"] },
  { name: "Biology Lab", type: "biology", capacity: 25, features: ["Microscopes", "Specimen storage", "Incubator", "Refrigerator"] },
  { name: "Computer Lab 1", type: "computer", capacity: 35, features: ["30 workstations", "Projector", "Printer access", "High-speed internet"] },
  { name: "Computer Lab 2", type: "computer", capacity: 30, features: ["25 workstations", "3D printer", "VR headsets"] }
];

const sampleBreakages: BreakageReport[] = [
  { id: "1", labName: "Chemistry Lab", date: new Date(Date.now() - 86400000 * 5).toISOString().split('T')[0], item: "Beaker (500ml)", status: "pending" },
  { id: "2", labName: "Physics Lab", date: new Date(Date.now() - 86400000 * 10).toISOString().split('T')[0], item: "Lens holder", status: "resolved" }
];

const TeacherLabViewer = () => {
  const { currentUser } = useSelector(selectUsers);
  const teacherName = currentUser ? `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() || (currentUser.email || "Staff Member") : "Staff Member";

  const [sessions, setSessions] = useState<LabSession[]>([]);
  const [breakages, setBreakages] = useState<BreakageReport[]>([]);
  const [sessionNotes, setSessionNotes] = useState<Record<string, SessionNote>>({});
  const [searchQuery, setSearchQuery] = useState("");
  const [filterLab, setFilterLab] = useState("all");
  const [filterTime] = useState("upcoming");
  const [darkMode, setDarkMode] = useState(false);
  const [selectedSession, setSelectedSession] = useState<LabSession | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [showSessionDetails, setShowSessionDetails] = useState(false);
  const [currentWeekStart, setCurrentWeekStart] = useState(getWeekStart(new Date()));

  useEffect(() => {
    const savedNotes = localStorage.getItem("teacherLabViewer_notes");
    
    if (savedNotes) {
      setSessionNotes(JSON.parse(savedNotes));
    }
  }, []);

  useEffect(() => {
    const demoData = sampleSessions;
    setSessions(demoData);
    setBreakages(sampleBreakages.filter(b => b.status === "pending"));
  }, [teacherName]);

  function getWeekStart(date: Date): Date {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  }



  const getLabColor = (type: string) => {
    const colors: Record<string, string> = {
      chemistry: "bg-blue-500",
      physics: "bg-purple-500",
      biology: "bg-green-500",
      computer: "bg-orange-500"
    };
    return colors[type] || "bg-muted";
  };


  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const isToday = (dateStr: string) => {
    return dateStr === new Date().toISOString().split('T')[0];
  };

  const isFuture = (dateStr: string) => {
    return dateStr >= new Date().toISOString().split('T')[0];
  };

  const getNextSession = () => {
    const now = new Date();
    const todayStr = now.toISOString().split('T')[0];
    const currentTime = now.getHours() * 60 + now.getMinutes();

    const upcoming = sessions
      .filter(s => {
        if (s.date > todayStr) return true;
        if (s.date === todayStr) {
          const [hours, mins] = s.startTime.split(':').map(Number);
          return hours * 60 + mins > currentTime;
        }
        return false;
      })
      .sort((a, b) => {
        if (a.date !== b.date) return a.date.localeCompare(b.date);
        return a.startTime.localeCompare(b.startTime);
      });

    return upcoming[0];
  };

  const getCountdown = (session: LabSession) => {
    const now = new Date();
    const sessionDate = new Date(session.date + 'T' + session.startTime);
    const diff = sessionDate.getTime() - now.getTime();

    if (diff <= 0) return "Now";

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  const getTodaySessions = () => {
    const today = new Date().toISOString().split('T')[0];
    return sessions.filter(s => s.date === today).sort((a, b) => a.startTime.localeCompare(b.startTime));
  };

  const getFilteredSessions = () => {
    let filtered = [...sessions];
    const today = new Date().toISOString().split('T')[0];

    if (filterTime === "upcoming") {
      filtered = filtered.filter(s => s.date >= today);
    } else if (filterTime === "past") {
      filtered = filtered.filter(s => s.date < today);
    }

    if (filterLab !== "all") {
      filtered = filtered.filter(s => s.labName === filterLab);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(s => 
        s.className.toLowerCase().includes(query) ||
        s.subject.toLowerCase().includes(query) ||
        s.labName.toLowerCase().includes(query) ||
        s.topic?.toLowerCase().includes(query)
      );
    }

    return filtered.sort((a, b) => {
      if (a.date !== b.date) return a.date.localeCompare(b.date);
      return a.startTime.localeCompare(b.startTime);
    });
  };

  const getWeekDays = () => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(currentWeekStart);
      day.setDate(day.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const getSessionsForDate = (date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    return sessions.filter(s => s.date === dateStr);
  };


  const exportToICS = () => {
    const upcoming = sessions.filter(s => isFuture(s.date));
    let icsContent = "BEGIN:VCALENDAR\nVERSION:2.0\nPRODID:-//Teacher Lab Viewer//EN\n";
    
    upcoming.forEach(session => {
      const startDate = session.date.replace(/-/g, '') + 'T' + session.startTime.replace(':', '') + '00';
      const endDate = session.date.replace(/-/g, '') + 'T' + session.endTime.replace(':', '') + '00';
      
      icsContent += `BEGIN:VEVENT\n`;
      icsContent += `DTSTART:${startDate}\n`;
      icsContent += `DTEND:${endDate}\n`;
      icsContent += `SUMMARY:${session.subject} - ${session.className}\n`;
      icsContent += `LOCATION:${session.labName}\n`;
      icsContent += `DESCRIPTION:${session.topic || session.subject}${session.notes ? '\\n' + session.notes : ''}\n`;
      icsContent += `END:VEVENT\n`;
    });
    
    icsContent += "END:VCALENDAR";
    
    const blob = new Blob([icsContent], { type: 'text/calendar' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `lab_schedule_${teacherName.replace(/\s/g, '_')}.ics`;
    a.click();
    URL.revokeObjectURL(url);
    
    toast({ title: "Exported", description: "Calendar file downloaded" });
  };

  const printSchedule = () => {
    window.print();
  };


  const nextSession = getNextSession();
  const todaySessions = getTodaySessions();
  const pendingBreakages = breakages.filter(b => b.status === "pending");

  return (
    <SidebarProvider>
      <div className="flex min-h-screen bg-gray-50/50 dark:bg-gray-900 w-full font-sans">
        <Teachersidebar />
        <main className="flex-1 flex flex-col w-full h-screen overflow-hidden">
          <Teacherheader />
          <div className="flex-1 overflow-auto p-4 sm:p-6 lg:p-8">
            <div className={`space-y-6 ${darkMode ? 'dark' : ''}`}>
              {/* Header */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h2 className="text-2xl font-bold flex items-center gap-2">
                    <FlaskConical className="h-6 w-6 text-primary" />
                    My Lab Schedule
                  </h2>
                  <p className="text-muted-foreground">Welcome, {teacherName}</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button variant="outline" size="sm" onClick={() => setDarkMode(!darkMode)}>
                    {darkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                  </Button>
                  <Button variant="outline" size="sm" onClick={printSchedule}>
                    <Printer className="h-4 w-4 mr-1" />
                    Print
                  </Button>
                  <Button variant="outline" size="sm" onClick={exportToICS}>
                    <CalendarPlus className="h-4 w-4 mr-1" />
                    iCal
                  </Button>
                </div>
              </div>

              {/* Next Session Countdown */}
              {nextSession && (
                <Card className="bg-primary/5 border-primary/20">
                  <CardContent className="py-4">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <div className="p-3 bg-primary/10 rounded-full">
                          <Timer className="h-6 w-6 text-primary" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Next Lab Session</p>
                          <p className="font-semibold">{nextSession.subject} - {nextSession.className}</p>
                          <p className="text-sm text-muted-foreground">
                            {formatDate(nextSession.date)} at {nextSession.startTime} • {nextSession.labName}
                          </p>
                        </div>
                      </div>
                      <div className="text-center px-4 py-2 bg-background rounded-lg">
                        <p className="text-2xl font-bold text-primary">{getCountdown(nextSession)}</p>
                        <p className="text-xs text-muted-foreground">until session</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Alerts */}
              {pendingBreakages.length > 0 && (
                <Card className="border-amber-500/50 bg-amber-500/5">
                  <CardContent className="py-3 flex items-center gap-3">
                    <AlertTriangle className="h-5 w-5 text-amber-500" />
                    <span className="text-sm">You have {pendingBreakages.length} unresolved breakage report(s)</span>
                    <Button variant="ghost" size="sm" className="ml-auto">View</Button>
                  </CardContent>
                </Card>
              )}

              <Tabs defaultValue="today" className="space-y-4">
                <TabsList className="grid grid-cols-4 w-full max-w-md">
                  <TabsTrigger value="today">Today</TabsTrigger>
                  <TabsTrigger value="upcoming">Upcoming</TabsTrigger>
                  <TabsTrigger value="calendar">Calendar</TabsTrigger>
                  <TabsTrigger value="search">Search</TabsTrigger>
                </TabsList>

                {/* Today Tab */}
                <TabsContent value="today" className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Calendar className="h-5 w-5" />
                        Today's Sessions
                        <Badge variant="secondary" className="ml-2">{todaySessions.length}</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {todaySessions.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                          <FlaskConical className="h-12 w-12 mx-auto mb-4 opacity-50" />
                          <p>No lab sessions scheduled for today</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {todaySessions.map(session => {
                            const note = sessionNotes[session.id];
                            return (
                              <Card 
                                key={session.id} 
                                className="cursor-pointer hover:shadow-md transition-shadow"
                                onClick={() => { setSelectedSession(session); setShowSessionDetails(true); }}
                              >
                                <CardContent className="p-4">
                                  <div className="flex items-start justify-between gap-4">
                                    <div className="flex items-start gap-3">
                                      <div className={`w-1 h-full min-h-[60px] rounded ${getLabColor(session.labType)}`} />
                                      <div>
                                        <div className="flex items-center gap-2 mb-1">
                                          <span className="font-medium">{session.period}</span>
                                          <span className="text-muted-foreground">•</span>
                                          <span className="text-muted-foreground">{session.startTime} - {session.endTime}</span>
                                        </div>
                                        <p className="font-semibold">{session.subject}</p>
                                        <p className="text-sm text-muted-foreground">{session.className} • {session.labName}</p>
                                        {session.topic && (
                                          <p className="text-sm mt-1">{session.topic}</p>
                                        )}
                                        {session.notes && (
                                          <p className="text-sm text-amber-600 mt-1 flex items-center gap-1">
                                            <StickyNote className="h-3 w-3" />
                                            {session.notes}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex flex-col items-end gap-2">
                                      {session.studentCount && (
                                        <Badge variant="outline" className="flex items-center gap-1">
                                          <Users className="h-3 w-3" />
                                          {session.studentCount}
                                        </Badge>
                                      )}
                                      {note && (
                                        <div className="flex gap-1">
                                          {note.checklist.prepared && <CheckCircle2 className="h-4 w-4 text-green-500" />}
                                          {note.checklist.checked && <CheckCircle2 className="h-4 w-4 text-blue-500" />}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </CardContent>
                              </Card>
                            );
                          })}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Lab Quick Info */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Lab Resources</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {labInfoData.slice(0, 4).map(lab => (
                          <div key={lab.name} className="p-3 border border-border/50 bg-card text-card-foreground shadow-sm rounded-lg">
                            <div className="flex items-center gap-2 mb-1">
                              <div className={`w-2 h-2 rounded-full ${getLabColor(lab.type)}`} />
                              <span className="font-medium text-sm">{lab.name}</span>
                            </div>
                            <p className="text-xs text-muted-foreground">{lab.capacity} seats • {lab.features[0]}</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Upcoming Tab */}
                <TabsContent value="upcoming" className="space-y-4">
                  <div className="flex gap-2">
                    <Select value={filterLab} onValueChange={setFilterLab}>
                      <SelectTrigger className="w-40">
                        <SelectValue placeholder="Filter by lab" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Labs</SelectItem>
                        {labInfoData.map(lab => (
                          <SelectItem key={lab.name} value={lab.name}>{lab.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-3">
                    {getFilteredSessions().filter(s => isFuture(s.date)).map(session => (
                      <Card 
                        key={session.id}
                        className={`cursor-pointer hover:shadow-md transition-shadow ${isToday(session.date) ? 'border-primary/50 bg-primary/5' : ''}`}
                        onClick={() => { setSelectedSession(session); setShowSessionDetails(true); }}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <div className={`w-1 h-full min-h-[50px] rounded ${getLabColor(session.labType)}`} />
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">{formatDate(session.date)}</span>
                                  {isToday(session.date) && <Badge className="bg-primary">Today</Badge>}
                                </div>
                                <span className="text-sm text-muted-foreground">{session.startTime} - {session.endTime}</span>
                              </div>
                              <p className="font-semibold">{session.subject} - {session.className}</p>
                              <p className="text-sm text-muted-foreground flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {session.labName}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                    
                    {getFilteredSessions().filter(s => isFuture(s.date)).length === 0 && (
                      <div className="text-center py-12 text-muted-foreground">
                        <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>No upcoming lab sessions</p>
                      </div>
                    )}
                  </div>
                </TabsContent>

                {/* Calendar Tab */}
                <TabsContent value="calendar" className="space-y-4">
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <Button variant="ghost" size="sm" onClick={() => {
                          const newDate = new Date(currentWeekStart);
                          newDate.setDate(newDate.getDate() - 7);
                          setCurrentWeekStart(newDate);
                        }}>
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <CardTitle className="text-lg">
                          {currentWeekStart.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                        </CardTitle>
                        <Button variant="ghost" size="sm" onClick={() => {
                          const newDate = new Date(currentWeekStart);
                          newDate.setDate(newDate.getDate() + 7);
                          setCurrentWeekStart(newDate);
                        }}>
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-7 gap-1">
                        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                          <div key={day} className="text-center text-xs font-medium text-muted-foreground py-2">
                            {day}
                          </div>
                        ))}
                        {getWeekDays().map(day => {
                          const daySessions = getSessionsForDate(day);
                          const isCurrentDay = day.toISOString().split('T')[0] === new Date().toISOString().split('T')[0];
                          const isSelected = day.toDateString() === selectedDate.toDateString();
                          
                          return (
                            <div
                              key={day.toISOString()}
                              className={`min-h-[80px] p-1 border rounded cursor-pointer hover:bg-accent/50 ${
                                isCurrentDay ? 'border-primary bg-primary/5' : ''
                              } ${isSelected ? 'ring-2 ring-primary ring-offset-2' : ''}`}
                              onClick={() => setSelectedDate(day)}
                            >
                              <div className={`text-sm font-medium text-center mb-1 ${isCurrentDay ? 'text-primary' : ''}`}>
                                {day.getDate()}
                              </div>
                              <div className="space-y-1">
                                {daySessions.slice(0, 2).map(session => (
                                  <div
                                    key={session.id}
                                    className={`text-xs p-1 rounded truncate ${getLabColor(session.labType)} text-white`}
                                  >
                                    {session.startTime} {session.subject}
                                  </div>
                                ))}
                                {daySessions.length > 2 && (
                                  <div className="text-xs text-center text-muted-foreground">
                                    +{daySessions.length - 2} more
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Search Tab */}
                <TabsContent value="search" className="space-y-4">
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search by class, subject, topic..."
                        className="pl-9"
                      />
                    </div>
                  </div>

                  <div className="space-y-3">
                    {getFilteredSessions().map(session => (
                      <Card 
                        key={session.id}
                        className="cursor-pointer hover:shadow-md transition-shadow"
                        onClick={() => { setSelectedSession(session); setShowSessionDetails(true); }}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <div className={`w-1 h-full min-h-[50px] rounded ${getLabColor(session.labType)}`} />
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">{formatDate(session.date)}</span>
                                  {isToday(session.date) && <Badge className="bg-primary">Today</Badge>}
                                </div>
                                <span className="text-sm text-muted-foreground">{session.startTime} - {session.endTime}</span>
                              </div>
                              <p className="font-semibold">{session.subject} - {session.className}</p>
                              <p className="text-sm text-muted-foreground flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {session.labName}
                              </p>
                              {session.topic && (
                                <p className="text-sm mt-1 text-muted-foreground">{session.topic}</p>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </TabsContent>
              </Tabs>

              {/* Session Details Dialog */}
              <Dialog open={showSessionDetails} onOpenChange={setShowSessionDetails}>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <FlaskConical className="h-5 w-5" />
                      Session Details
                    </DialogTitle>
                  </DialogHeader>
                  {selectedSession && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm text-muted-foreground">Subject</label>
                        <p className="font-medium">{selectedSession.subject}</p>
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground">Class</label>
                        <p className="font-medium">{selectedSession.className}</p>
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground">Date & Time</label>
                        <p className="font-medium">{formatDate(selectedSession.date)} • {selectedSession.startTime}</p>
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground">Lab</label>
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${getLabColor(selectedSession.labType)}`} />
                          <p className="font-medium">{selectedSession.labName}</p>
                        </div>
                      </div>
                      <div className="col-span-2">
                        <label className="text-sm text-muted-foreground">Topic</label>
                        <p className="font-medium">{selectedSession.topic || "No topic specified"}</p>
                      </div>
                      {selectedSession.notes && (
                        <div className="col-span-2 bg-amber-50 dark:bg-amber-900/10 p-3 rounded-lg border border-amber-100 dark:border-amber-900/20">
                          <label className="text-sm text-amber-600 font-medium flex items-center gap-1 mb-1">
                            <StickyNote className="h-3 w-3" />
                            Scheduler Notes
                          </label>
                          <p className="text-sm">{selectedSession.notes}</p>
                        </div>
                      )}
                      
                      {/* Lab Features */}
                      <div className="col-span-2 mt-2">
                         <div className="flex flex-wrap gap-1">
                           {(labInfoData.find(l => l.name === selectedSession.labName)?.features || []).map(f => (
                             <Badge key={f} variant="outline" className="text-xs">{f}</Badge>
                           ))}
                         </div>
                      </div>
                    </div>
                  )}
        </DialogContent>
      </Dialog>
    </div>
      </div>
        </main>
      </div>
    </SidebarProvider>
  );
};

export default TeacherLabViewer;